import React from 'react';
import { Box, Chip, Skeleton, Tooltip, Typography } from '@mui/material';
import {
    MISSING_REQUIREMENT_LABEL,
    MissingRequirement,
    PendingCounts,
} from './accounting-export-rules';

// The checklist the month is handed off against: which export-eligible documents
// still have missing requirements. Canceled documents are never counted — they do
// not require any. A document missing two items counts once in `documents` and
// once under each item, which is why the chips can add up to more than the
// headline number.

const REQUIREMENT_ORDER: MissingRequirement[] = [
    'FOLIO',
    'SUPPLEMENT',
    'CREDIT_NOTE',
    'TAX',
];

function PendingLine({
    title,
    counts,
    loading,
}: {
    title: string;
    counts: PendingCounts;
    loading?: boolean;
}) {
    const chips = REQUIREMENT_ORDER.filter(
        (requirement) => counts.byRequirement[requirement] > 0,
    );

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
                variant={'caption'}
                sx={{ color: 'text.secondary', fontWeight: 600, minWidth: 60 }}
            >
                {title}
            </Typography>
            {loading ? (
                <Skeleton variant={'text'} width={140} sx={{ transform: 'none', height: 24 }} />
            ) : counts.documents === 0 ? (
                <Typography variant={'body2'} sx={{ color: 'success.main', fontWeight: 600 }}>
                    Sin pendientes
                </Typography>
            ) : (
                <>
                    <Tooltip
                        arrow
                        title={
                            'Documentos con al menos un faltante. Un documento con dos faltantes cuenta una sola vez aquí.'
                        }
                    >
                        <Typography
                            variant={'body2'}
                            sx={{ color: 'warning.main', fontWeight: 700 }}
                        >
                            {`${counts.documents} documento${
                                counts.documents === 1 ? '' : 's'
                            }`}
                        </Typography>
                    </Tooltip>
                    {chips.map((requirement) => (
                        <Chip
                            key={requirement}
                            size={'small'}
                            variant={'outlined'}
                            color={'warning'}
                            label={`${MISSING_REQUIREMENT_LABEL[requirement]}: ${counts.byRequirement[requirement]}`}
                        />
                    ))}
                </>
            )}
        </Box>
    );
}

// Only the bank-linked pendientes are summarized here: their transfer already
// moved, so the paperwork is overdue. The emitidas pendientes are surfaced as
// per-row alerts on their own tabs instead of being counted here.
export function AccountingPendingPanel({
    bankSalesCounts,
    bankExpensesCounts,
    loading,
}: {
    bankSalesCounts: PendingCounts;
    bankExpensesCounts: PendingCounts;
    loading?: boolean;
}) {
    return (
        <Box>
            <Tooltip
                arrow
                title={
                    'Documentos ya ligados a una transferencia y que aún deben papelería. Los cancelados no requieren papelería y no se cuentan.'
                }
            >
                <Typography
                    variant={'overline'}
                    sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 1 }}
                >
                    Documentos pendientes (bancos)
                </Typography>
            </Tooltip>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 0.5 }}>
                <PendingLine title={'Ventas'} counts={bankSalesCounts} loading={loading} />
                <PendingLine title={'Gastos'} counts={bankExpensesCounts} loading={loading} />
            </Box>
        </Box>
    );
}
